import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Package, MapPin } from "lucide-react";
import { trackOrder } from "../lib/api";
import { toast } from "sonner";

export default function TrackOrder() {
  const [form, setForm] = useState({ order_id: "", email: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await trackOrder(form);
      setResult(r);
    } catch {
      toast.error("Could not locate order.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="track-order-page">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <div className="label-eyebrow mb-4">Order Tracking</div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tight leading-none">
            Where is my <span className="italic text-gradient-gold">box</span>?
          </h1>
        </div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="bg-ink-900 border border-white/10 p-10 space-y-5"
          data-testid="track-order-form"
        >
          <input
            required
            value={form.order_id}
            onChange={(e) => setForm({ ...form, order_id: e.target.value })}
            placeholder="Order ID (e.g., BX-2026-0042)"
            className="w-full bg-ink-950 border border-white/10 text-bone px-4 py-3 text-sm font-light focus:outline-none focus:border-gold"
            data-testid="track-order-id-input"
          />
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Order email"
            className="w-full bg-ink-950 border border-white/10 text-bone px-4 py-3 text-sm font-light focus:outline-none focus:border-gold"
            data-testid="track-order-email-input"
          />
          <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="track-order-submit-btn">
            {loading ? "Tracking..." : "Track order"}
          </button>
        </motion.form>

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10 bg-ink-900 border border-gold/30 p-10"
            data-testid="track-order-result"
          >
            <div className="flex items-center gap-3 mb-6">
              <Package className="w-5 h-5 text-gold" />
              <div>
                <div className="label-eyebrow text-mist">Order {result.order_id}</div>
                <p className="font-serif text-2xl text-bone capitalize">{result.status.replace(/-/g, " ")}</p>
              </div>
            </div>
            <div className="space-y-4">
              {result.timeline.map((t, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${t.done ? "bg-gold text-ink-950" : "border border-white/20 text-mist"}`}>
                    {t.done ? <Check className="w-3 h-3" /> : <span className="text-xs">{i + 1}</span>}
                  </div>
                  <span className={t.done ? "text-bone" : "text-mist"}>{t.label}</span>
                </div>
              ))}
            </div>
            <p className="text-mist text-xs mt-6 font-light">
              Estimated delivery: <span className="text-gold">{result.estimated_delivery}</span>
            </p>
            <p
              className="flex items-center gap-1.5 text-mist/70 text-[11px] font-light tracking-wider mt-3 pt-3 border-t border-white/5"
              data-testid="dispatch-origin-result"
            >
              <MapPin className="w-3 h-3 text-gold/70" />
              Dispatched from <span className="text-bone/80">Dwarka, New Delhi</span> · discreet packaging
            </p>
          </motion.div>
        )}

        <p
          className="flex items-center justify-center gap-1.5 text-mist/70 text-[11px] font-light tracking-[0.18em] uppercase mt-10"
          data-testid="dispatch-origin-footer"
        >
          <MapPin className="w-3 h-3 text-gold/70" />
          All orders ship discreetly from Dwarka, New Delhi · India
        </p>
      </div>
    </div>
  );
}
